import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { isBlank } from '@app/utils/string';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject, Subject } from 'rxjs';
import { ComparisonEntity, ComparisonGroup } from './comparison-data';

/**
 * Used in mapping of item `name` to this object containing values found for each comparison entity.
 * Values are placed into arrays and index of the value corresponds to the index of the comparison entity.
 */
interface ComparedItemOverEntities {
  hasItem: boolean[];
  isItemOptional: boolean[];
  /** explicit value to be shown in the view */
  explicitValue: string[];
  additionalInfo: string[];
  orderIndex: number;
}

/** Item amounts per comparison group and entity */
interface Amounts {
  total: number[][];
  optionalAmounts: number[][] | null;
  filteredAmounts: number[][] | null;
  filteredOptionalAmounts: number[][] | null;
}

@UntilDestroy()
@Component({
  selector: 'portal-comparison',
  templateUrl: './comparison.component.html',
  styleUrls: ['./comparison.component.scss'],
  standalone: false,
})
export class ComparisonComponent implements OnInit, OnChanges {
  @Input()
  data: ComparisonGroup | ComparisonGroup[];

  /** will be `true` if there are more than 15 items to compare */
  public showFilterInput: boolean = false;

  /** subject per comparison group */
  public comparisonSubject: string[] = [];
  /** entity names per comparison group */
  public entityNames: string[][] = [];
  /** entity descriptions per comparison group */
  public entityDescriptions: string[][] = [];

  /** sorted array of all items (per comparison group) */
  public allComparisonItemNames: string[][] = [];
  /** filtered version of `allComparisonItemNames` for the view. */
  public comparisonItemNamesFiltered$: Subject<string[][]> = new ReplaySubject(1);

  /** mapping of item name, per comparison group, to compared item values in corresponding entities */
  public comparedItems: Map<string, ComparedItemOverEntities>[] = [];

  /** items amount per comparison group and entity */
  public amounts: Amounts = {
    total: [],
    optionalAmounts: [],
    filteredAmounts: [],
    filteredOptionalAmounts: [],
  };

  public showLegend: boolean = true;
  public showOptionallyIncludedItemsLegend: boolean = false;

  private filterValue: string = '';

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      this.doComparison(this.data);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('data' in changes) {
      this.doComparison(changes.data.currentValue as ComparisonGroup | ComparisonGroup[]);
    }
  }

  doComparison(data: ComparisonGroup | ComparisonGroup[]) {
    if (data == null) {
      return;
    }
    // constructing comparison groups
    const comparisonGroups: ComparisonGroup[] = [];
    if (Array.isArray(data)) {
      comparisonGroups.push(...data);
    } else {
      comparisonGroups.push(data);
    }

    // optional items might not be used, hence no need to show the legend for them
    let showOptionalItemLegend = false;

    for (let compGroupIx = 0; compGroupIx < comparisonGroups.length; compGroupIx++) {
      const group = comparisonGroups[compGroupIx];
      this.comparedItems[compGroupIx] = new Map();
      this.entityNames[compGroupIx] = [];
      this.entityDescriptions[compGroupIx] = [];
      this.amounts.total[compGroupIx] = [];
      this.amounts.optionalAmounts[compGroupIx] = [];
      this.amounts.filteredAmounts[compGroupIx] = [];
      this.amounts.filteredOptionalAmounts[compGroupIx] = [];

      this.comparisonSubject[compGroupIx] = group.subject;

      group.entities.forEach((e) => {
        this.entityNames[compGroupIx].push(e.name);
        this.entityDescriptions[compGroupIx].push(e.description);
        if (e.items[0]?.value !== undefined) {
          const amount = e.items.filter((x) => x.value != null).length;
          this.amounts.total[compGroupIx].push(amount);
        } else {
          this.amounts.total[compGroupIx].push(e.items.length);
        }
      });

      group.entities.forEach((entity, entityIx) => {
        entity.items.forEach((item) => {
          const itemName = this.translateService.instant(item.name);
          const cd: ComparedItemOverEntities =
            this.comparedItems[compGroupIx].get(itemName) ??
            this.initialComparedDataForItemMapping(group.entities);
          const itemIsSet = item.value !== null;
          cd.hasItem[entityIx] = itemIsSet;
          cd.isItemOptional[entityIx] = item.isOptional ?? false;
          if (showOptionalItemLegend === false && cd.isItemOptional[entityIx] === true) {
            showOptionalItemLegend = true;
          }
          if (item.value != null) {
            cd.explicitValue[entityIx] =
              item.translateValue === true
                ? this.translateService.instant(item.value as string)
                : (item.value as string);
          }
          if (item.info != null) {
            cd.additionalInfo[entityIx] = item.info;
          }
          if (cd.orderIndex === 0 && item.orderIndex > 0) {
            cd.orderIndex = item.orderIndex;
          }
          this.comparedItems[compGroupIx].set(itemName, cd);
        });

        entity.itemsRemoved?.forEach((item) => {
          const itemName = this.translateService.instant(item.name);
          const cd: ComparedItemOverEntities =
            this.comparedItems[compGroupIx].get(itemName) ??
            this.initialComparedDataForItemMapping(group.entities);
          cd.hasItem[entityIx] = false;
          if (item.info != null) {
            cd.additionalInfo[entityIx] = item.info;
          }

          this.comparedItems[compGroupIx].set(itemName, cd);
        });

        // sorting of items by order-index and their names (lexically).
        this.allComparisonItemNames[compGroupIx] = Array.from(this.comparedItems[compGroupIx].keys()).sort(
          (a, b) => {
            const aoi = this.comparedItems[compGroupIx].get(a).orderIndex;
            const boi = this.comparedItems[compGroupIx].get(b).orderIndex;
            if (aoi > 0 && boi > 0) {
              if (aoi > boi) {
                return 1;
              } else if (aoi < boi) {
                return -1;
              } else {
                return a.localeCompare(b);
              }
            } else if (aoi > 0 || boi > 0) {
              if (aoi > boi) {
                return -1;
              }
              return 1;
            }
            return a.localeCompare(b);
          }
        );
      });
    }
    this.showOptionallyIncludedItemsLegend = showOptionalItemLegend;

    // filter input element should be shown when there are more than 15 items to be compared.
    let countOfItems = 0;
    this.showFilterInput = comparisonGroups.some((g) =>
      g.entities.some((e) => {
        countOfItems += e.items.length;
        if (countOfItems > 15) {
          return true;
        } else {
          return false;
        }
      })
    );

    // performing filtering of visible items based on filter value. the result is emitted .
    if (isBlank(this.filterValue)) {
      this.calculateOptionalAndFilteredCounts(comparisonGroups, null);
      this.comparisonItemNamesFiltered$.next(this.allComparisonItemNames);
    } else {
      const filteredItemsNames = this.allComparisonItemNames.map((g) =>
        g.filter((i) => i.toLowerCase().indexOf(this.filterValue) > -1)
      );
      this.calculateOptionalAndFilteredCounts(comparisonGroups, filteredItemsNames);
      this.comparisonItemNamesFiltered$.next(filteredItemsNames);
    }
  }

  // `filteredItemsNames` are per comparison group (array of array)
  private calculateOptionalAndFilteredCounts(
    comparisonGroups: ComparisonGroup[],
    filteredItemsNames?: string[][]
  ): void {
    this.amounts.optionalAmounts = [];
    this.amounts.filteredAmounts = [];
    this.amounts.filteredOptionalAmounts = [];

    for (let groupIx = 0; groupIx < comparisonGroups.length; groupIx++) {
      const group = comparisonGroups[groupIx];
      // const comparedItemsOverEntitiesMap = this.comparedItems[groupIx];
      this.amounts.optionalAmounts[groupIx] = Array(group.entities.length).fill(null);
      this.amounts.filteredAmounts[groupIx] = Array(group.entities.length).fill(null);
      this.amounts.filteredOptionalAmounts[groupIx] = Array(group.entities.length).fill(null);

      for (let entityIx = 0; entityIx < group.entities.length; entityIx++) {
        const entity = group.entities[entityIx];
        const optionalItemsCount = entity.items.filter((item) => item.isOptional === true).length;
        this.amounts.optionalAmounts[groupIx][entityIx] =
          optionalItemsCount === 0 ? null : optionalItemsCount;
      }
    }
    if (filteredItemsNames == null) {
      return;
    }

    const itemsKeys = filteredItemsNames;
    for (let groupIx = 0; groupIx < comparisonGroups.length; groupIx++) {
      const comparedItemsOverEntitiesMap = this.comparedItems[groupIx];

      for (let itemName of itemsKeys[groupIx]) {
        if (comparedItemsOverEntitiesMap.has(itemName) === true) {
          const itemsOverEntities = comparedItemsOverEntitiesMap.get(itemName);
          for (let ix = 0; ix < itemsOverEntities.hasItem.length; ix++) {
            const hasItem = itemsOverEntities.hasItem[ix];
            if (hasItem === true) {
              this.amounts.filteredAmounts[groupIx][ix] = this.amounts.filteredAmounts[groupIx][ix] + 1;
              if (itemsOverEntities.isItemOptional[ix] === true)
                this.amounts.filteredOptionalAmounts[groupIx][ix] =
                  this.amounts.filteredOptionalAmounts[groupIx][ix] + 1;
            }
          }
        }
      }
    }
  }

  private initialComparedDataForItemMapping(entities: ComparisonEntity[]): ComparedItemOverEntities {
    const result = {
      hasItem: [] as boolean[],
      isItemOptional: [] as boolean[],
      explicitValue: [] as string[],
      additionalInfo: [] as string[],
      orderIndex: 0,
    };
    entities.forEach((_, index) => {
      result.hasItem[index] = false;
      result.isItemOptional[index] = false;
      result.explicitValue[index] = null;
      result.additionalInfo[index] = null;
    });

    return result;
  }

  filterBy(value: string): void {
    value = value?.trim().toLowerCase();
    if (value !== this.filterValue) {
      this.filterValue = value;
      this.doComparison(this.data);
    }
  }
}
